import React from 'react';
import dayjs from 'dayjs';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format/money';
import { formatAmountInWordsPlain } from '@/utils/amountInWords';
import type { PayrollPayslip, PayslipCompany, PayslipDocument } from '@/services/hrms.service';
import './PayrollPayslipSheet.css';

const amt = (n: number) => formatMoney(n, PRINT_PLAIN_AMOUNT_PROFILE);
const date = (d: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : '—');
const RELEASED = ['Approved', 'Closed', 'Locked', 'Paid'];

function CompanyHeader({ company, slip }: { company: PayslipCompany; slip: PayrollPayslip }) {
  const titleBox = (
    <div className="ps-title-box">
      <div className="ps-title">PAYSLIP</div>
      <div className="ps-title-month">{slip.period_label}</div>
      <div className="ps-title-ref">{slip.reference}</div>
    </div>
  );
  if (company.letterhead_header) {
    return (
      <>
        <img className="ps-letterhead" src={company.letterhead_header} alt="" />
        <div className="ps-head ps-head--compact">
          <div />
          {titleBox}
        </div>
      </>
    );
  }
  return (
    <div className="ps-head">
      <div className="ps-brand">
        {company.logo && <img className="ps-logo" src={company.logo} alt="" />}
        <div>
          <div className="ps-company">{company.name}</div>
          {company.address && <div className="ps-company-line">{company.address}</div>}
          <div className="ps-company-line">
            {[
              company.phone && `Tel ${company.phone}`,
              company.email,
              company.tax_registration_number && `TRN ${company.tax_registration_number}`,
            ]
              .filter(Boolean)
              .join('  ·  ')}
          </div>
        </div>
      </div>
      {titleBox}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="ps-field">
      <div className="ps-field-label">{label}</div>
      <div className="ps-field-value">{value || '—'}</div>
    </div>
  );
}

function AmountTable({
  title,
  rows,
  totalLabel,
  total,
  minRows,
  negative,
}: {
  title: string;
  rows: PayrollPayslip['earnings'];
  totalLabel: string;
  total: number;
  minRows: number;
  negative?: boolean;
}) {
  const fillers = Math.max(0, minRows - rows.length);
  return (
    <table className="ps-table">
      <thead>
        <tr>
          <th>{title}</th>
          <th className="ps-detail">Basis</th>
          <th className="ps-num">Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td colSpan={3} className="ps-empty">
              None
            </td>
          </tr>
        )}
        {rows.map((r, i) => (
          <tr key={`${r.name}-${i}`}>
            <td>{r.name}</td>
            <td className="ps-detail">{r.detail}</td>
            <td className="ps-num">{amt(r.amount)}</td>
          </tr>
        ))}
        {Array.from({ length: rows.length === 0 ? Math.max(0, fillers - 1) : fillers }).map((_, i) => (
          <tr key={`f-${i}`} className="ps-filler">
            <td colSpan={3}>&nbsp;</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2}>{totalLabel}</td>
          <td className="ps-num">{negative && total ? `-${amt(total)}` : amt(total)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

function PayslipPage({ company, slip }: { company: PayslipCompany; slip: PayrollPayslip }) {
  const e = slip.employee;
  const a = slip.attendance;
  const rows = Math.max(slip.earnings.length, slip.deductions.length, 3);
  const released = RELEASED.includes(slip.run_status);
  const tiles: Array<[string, number]> = [
    ['Calendar days', a.calendar],
    ['Paid days', a.payable],
    ['Present', a.present],
    ['Absent', a.absent],
    ['Paid leave', a.leave],
    ['Unpaid leave', a.unpaid_leave],
    ['Holidays', a.holiday],
    ['Weekly off', a.weekly_off],
    ['OT hours', a.ot_hours],
  ];
  return (
    <div className="print-doc-a4 ps-page">
      {!released && <div className="ps-watermark">DRAFT — NOT APPROVED</div>}
      <div className="ps-sheet">
        <CompanyHeader company={company} slip={slip} />

        <div className="ps-band">
          <span>
            Pay period <b>{date(slip.period_from)}</b> to <b>{date(slip.period_to)}</b>
          </span>
          <span>
            Pay date <b>{date(slip.payment_date)}</b>
          </span>
          <span>
            Currency <b>{slip.currency}</b>
          </span>
        </div>

        <div className="ps-section-title">Employee details</div>
        <div className="ps-fields">
          <Field label="Employee ID" value={e.code} />
          <Field label="Employee name" value={<b>{e.name}</b>} />
          <Field label="Designation" value={e.designation} />
          <Field label="Department" value={e.department} />
          <Field label="Date of joining" value={e.date_of_joining ? date(e.date_of_joining) : null} />
          <Field label="Nationality" value={e.nationality} />
          {e.pan_number ? <Field label="PAN" value={e.pan_number} /> : null}
          {e.uan_number ? <Field label="UAN" value={e.uan_number} /> : null}
          {e.esi_number ? <Field label="ESI no." value={e.esi_number} /> : null}
          {e.labour_card_number ? <Field label="Labour card no." value={e.labour_card_number} /> : null}
          {e.wps_person_code ? <Field label="WPS person ID" value={e.wps_person_code} /> : null}
          <Field label="Payment mode" value={e.payment_method} />
          <Field label="Bank" value={e.bank_name} />
          <Field
            label={e.ifsc ? 'Account / IFSC' : 'IBAN / Account'}
            value={e.ifsc ? `${e.account || '—'} / ${e.ifsc}` : e.account}
          />
          <Field label="Monthly salary" value={`${slip.currency} ${amt(slip.monthly_salary)}`} />
        </div>

        <div className="ps-section-title">Attendance summary</div>
        <div className="ps-tiles">
          {tiles.map(([label, value]) => (
            <div key={label} className="ps-tile">
              <div className="ps-tile-value">{value || 0}</div>
              <div className="ps-tile-label">{label}</div>
            </div>
          ))}
        </div>

        <div className="ps-columns">
          <AmountTable
            title="Earnings"
            rows={slip.earnings}
            totalLabel="Total earnings"
            total={slip.gross}
            minRows={rows}
          />
          <AmountTable
            title="Deductions"
            rows={slip.deductions}
            totalLabel="Total deductions"
            total={slip.total_deductions}
            minRows={rows}
            negative
          />
        </div>

        <div className="ps-net">
          <div>
            <div className="ps-net-label">Net pay</div>
            <div className="ps-net-words">
              {slip.currency} {formatAmountInWordsPlain(slip.net)}
            </div>
          </div>
          <div className="ps-net-amount">
            {slip.currency} {amt(slip.net)}
          </div>
        </div>
        <div className="ps-net-formula">
          Gross {amt(slip.gross)} − Deductions {amt(slip.total_deductions)} = Net {amt(slip.net)} · Daily rate{' '}
          {amt(slip.daily_rate)} (monthly salary ÷ 30)
        </div>

        {(slip.employer_contributions?.length || 0) > 0 && (
          <>
            <div className="ps-section-title">Employer contributions (company cost — not deducted from net)</div>
            <table className="ps-table">
              <thead>
                <tr>
                  <th>Contribution</th>
                  <th className="ps-detail">Note</th>
                  <th className="ps-num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {slip.employer_contributions!.map((r, i) => (
                  <tr key={`${r.name}-${i}`}>
                    <td>{r.name}</td>
                    <td className="ps-detail">{r.detail}</td>
                    <td className="ps-num">{amt(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>Total employer cost</td>
                  <td className="ps-num">{amt(slip.employer_total || 0)}</td>
                </tr>
              </tfoot>
            </table>
          </>
        )}

        <div className="ps-boxes">
          <div className="ps-box">
            <div className="ps-box-title">Year to date ({slip.period_label.slice(-4)})</div>
            <div className="ps-kv">
              <span>Months paid</span>
              <b>{slip.ytd.months}</b>
            </div>
            <div className="ps-kv">
              <span>Gross</span>
              <b>{amt(slip.ytd.gross)}</b>
            </div>
            <div className="ps-kv">
              <span>Deductions</span>
              <b>{amt(slip.ytd.deductions)}</b>
            </div>
            <div className="ps-kv">
              <span>Net</span>
              <b>{amt(slip.ytd.net)}</b>
            </div>
          </div>
          <div className="ps-box">
            <div className="ps-box-title">Loans & advances</div>
            {slip.loans.length === 0 ? (
              <div className="ps-muted">No active loans</div>
            ) : (
              slip.loans.map((l) => (
                <div key={l.loan_number} className="ps-kv">
                  <span>
                    {l.loan_number} ({l.loan_type})
                  </span>
                  <b>Balance {amt(l.outstanding)}</b>
                </div>
              ))
            )}
          </div>
          <div className="ps-box">
            <div className="ps-box-title">Leave balance</div>
            {slip.leave_balances.length === 0 ? (
              <div className="ps-muted">Not available</div>
            ) : (
              slip.leave_balances.map((l) => (
                <div key={l.leave_type} className="ps-kv">
                  <span>{l.leave_type}</span>
                  <b>{l.balance} day(s)</b>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="ps-footer">
          <div>
            This is a computer-generated payslip and does not require a signature. Please report any discrepancy to HR
            within 7 days.
          </div>
          <div>
            Confidential · Payroll run {slip.run_number} · Generated {dayjs().format('DD MMM YYYY HH:mm')}
          </div>
        </div>
        {company.letterhead_footer && <img className="ps-letterhead" src={company.letterhead_footer} alt="" />}
      </div>
    </div>
  );
}

export const PayrollPayslipSheet: React.FC<{ doc: PayslipDocument; onClose: () => void }> = ({ doc, onClose }) => {
  const first = doc.slips[0];
  const fileName =
    doc.slips.length === 1 && first
      ? `payslip-${first.employee.code}-${first.run_number}`
      : `payslips-${first?.run_number || ''}`;
  return (
    <PrintPreviewChrome
      title={doc.slips.length === 1 ? `Payslip — ${first?.employee.name}` : `Payslips (${doc.slips.length})`}
      fileName={fileName}
      onBack={onClose}
    >
      {doc.slips.map((s) => (
        <PayslipPage key={s.employee.id} company={doc.company} slip={s} />
      ))}
    </PrintPreviewChrome>
  );
};

export default PayrollPayslipSheet;
